import { useEffect, useState } from "react";

import { useParams } from "react-router-dom";

import {
  InventoryObject,
  InventoryOperation,
  InventoryAbsoluteData,
  Student,
  CreateUserPaymentModel,
  Note,
  User,
} from "../apis/armoryApi";
import { useArmory, useArmoryDispatch } from "../contexts/ArmoryContext";
import { useArmoryApi } from "../hooks/useArmoryApi";

import { Button } from "primereact/button";
import { Fieldset, FieldsetToggleEvent } from "primereact/fieldset";

import CheckInPanel from "../components/CheckInPanel";
import CheckOutPanel from "../components/CheckOutPanel";
import AssetDetailsPanel from "../components/AssetDetailsPanel";
import AssetAbsoluteDataPanel from "../components/AssetAbsoluteDataPanel";
import AssetHistoryPanel from "../components/AssetHistoryPanel";
import AssetPaymentsPanel from "../components/AssetPaymentsPanel";
import StudentSearchAutoComplete from "../components/StudentSearchAutoComplete";
import { Dialog } from "primereact/dialog";
import { InputTextarea } from "primereact/inputtextarea";
import { InputNumber } from "primereact/inputnumber";
import NotesPanel from "../components/NotesPanel";

interface AssetDataListElementProps {
  label: string;
  value: any;
}

const AssetDataListElement: React.FC<AssetDataListElementProps> = ({
  label,
  value,
}) => {
  return (
    <li className="flex align-items-center py-3 px-2 border-top-1 border-300 flex-wrap">
      <div className="text-500 w-6 md:w-3 font-medium">{label}</div>
      <div className="text-900 w-full md:w-7 md:flex-order-0 flex-order-1">
        {value}
      </div>
    </li>
  );
};

const AssetManagementPage: React.FC = () => {
  const [currentInventoryObject, setCurrentInventoryObject] =
    useState<InventoryObject>();
  const [historyCollapsed, setHistoryCollapsed] = useState(true);
  const [absoluteCollapsed, setAbsoluteCollapsed] = useState(true);
  const [paymentsCollapsed, setPaymentsCollapsed] = useState(true);
  const [notesCollapsed, setNotesCollapsed] = useState(true);
  const [assetHistory, setAssetHistory] = useState<InventoryOperation[]>();
  const [absoluteData, setAbsoluteData] = useState<InventoryAbsoluteData>();
  const { assetSerial } = useParams();
  const state = useArmory();
  const { isCursorLoading } = useArmory();
  const dispatch = useArmoryDispatch();
  const {
    getInventoryObject,
    getAssetHistory,
    getAbsoluteData,
    reportDamaged,
    reportMissing,
    createUserPayment,
	addAssetNote,
	getUserById
  } = useArmoryApi();
  const [damageDialogVisible, setDamageDialogVisible] = useState(false);
  const [damageDescription, setDamageDescription] = useState("");
  const [missingDialogVisible, setMissingDialogVisible] = useState(false);
  const [missingDetails, setMissingDetails] = useState("");
  const [paymentDialogVisible, setPaymentDialogVisible] = useState(false);
  const [paymentStudent, setPaymentStudent] = useState<Student | null>(null);
  const [paymentAmount, setPaymentAmount] = useState<number | null>(null);
  const [paymentNotes, setPaymentNotes] = useState("");
  const [currentUser, setCurrentUser] = useState<User>();

  //This triggers a page update whenever an action is added to the log
  useEffect(() => {
    if (!historyCollapsed) {
      getCurrentAssetHistory();
    }

    getCurrentInventoryObject();
  }, [state.log]);

  const SetCursorLoading = (isLoading: boolean) => {
    dispatch({
      type: "SET_VALUE",
      payload: { key: "isCursorLoading", value: isLoading },
    });
  }

  const getCurrentInventoryObject = async () => {
    if (assetSerial) {
      const apiResponse = await getInventoryObject(assetSerial);

      if (apiResponse.data) {
        setCurrentInventoryObject(apiResponse.data);
      }
    }
  };

  const getCurrentAssetHistory = async () => {
    if (currentInventoryObject) {
      const apiResponse = await getAssetHistory(currentInventoryObject.id);

      if (apiResponse.data) {
        setAssetHistory(apiResponse.data);
      }
    }
  };

  const handleAssetHistoryToggle = (event: FieldsetToggleEvent) => {
    getCurrentAssetHistory();

    setHistoryCollapsed(!historyCollapsed);
  };

  const handleAbsoluteToggle = (event: FieldsetToggleEvent) => {
    const getCurrentAbsoluteData = async () => {
      if (assetSerial) {
        const apiResponse = await getAbsoluteData(assetSerial);

        if (apiResponse.data) {
          setAbsoluteData(apiResponse.data);
        }
      }
    };

    getCurrentAbsoluteData();

    setAbsoluteCollapsed(!absoluteCollapsed);
  };

  const handlePaymentsToggle = (event: FieldsetToggleEvent) => {
    setPaymentsCollapsed(!paymentsCollapsed);
  };

  const handleNotesToggle = (event: FieldsetToggleEvent) => {
	  setNotesCollapsed(!notesCollapsed);
  };

  const handleReportDamaged = async () => {
    SetCursorLoading(true);

    if (damageDescription) {
      if (assetSerial) {
        const apiResponse = await reportDamaged(assetSerial, damageDescription);

        if (apiResponse.statusCode === 200) {
          const successMsg: string = `Asset: ${assetSerial} - Report Damaged succeeded.`;

		  const newEntry: Note = {
			inventoryObjectId: currentInventoryObject?.id ?? 0,
			text: damageDescription,
			submitterName: `${currentUser?.firstName ?? ""} ${currentUser?.preferredName ? "\"" + currentUser.preferredName + "\" " : ""}${currentUser?.lastName ?? ""}`,
			submitterId: state.userGid,
			timestamp: new Date().toISOString(),
		  };

		  addAssetNote(newEntry);

          dispatch({
            type: "SHOW_TOAST",
            payload: {
              severity: "success",
              summary: "Success",
              detail: successMsg,
              life: 3000,
            },
          });
          dispatch({ type: "ADD_LOG_ENTRY", payload: { message: successMsg } });
          setDamageDialogVisible(false);
        } else {
          const errorMsg: string = `Asset: ${assetSerial} - Report Damaged failed. ${apiResponse.error}`;
          dispatch({
            type: "SHOW_TOAST",
            payload: {
              severity: "error",
              summary: "Error",
              detail: errorMsg,
              life: 3000,
            },
          });
          dispatch({ type: "ADD_LOG_ENTRY", payload: { message: errorMsg } });
        }
      } else {
        dispatch({
          type: "ADD_LOG_ENTRY",
          payload: {
            message: "Error: Missing asset serial when reporting damaged.",
          },
        });
      }
    } else {
      dispatch({
        type: "SHOW_TOAST",
        payload: {
          severity: "error",
          summary: "Error",
          detail: "A description of the damage is required",
          life: 3000,
        },
      });
    }

    setTimeout(() => {
      SetCursorLoading(false);
    }, 0);
  };

  const handleReportMissing = async () => {
    SetCursorLoading(true);

    if (missingDetails) {
      if (assetSerial) {
        const apiResponse = await reportMissing(assetSerial, missingDetails);

        if (apiResponse.statusCode === 200) {
          const successMsg: string = `Asset: ${assetSerial} - Report Missing succeeded.`;
          dispatch({
            type: "SHOW_TOAST",
            payload: {
              severity: "success",
              summary: "Success",
              detail: successMsg,
              life: 3000,
            },
          });
          dispatch({ type: "ADD_LOG_ENTRY", payload: { message: successMsg } });
          setMissingDialogVisible(false);
        } else {
          const errorMsg: string = `Asset: ${assetSerial} - Report Missing failed. ${apiResponse.error}`;
          dispatch({
            type: "SHOW_TOAST",
            payload: {
              severity: "error",
              summary: "Error",
              detail: errorMsg,
              life: 3000,
            },
          });
          dispatch({ type: "ADD_LOG_ENTRY", payload: { message: errorMsg } });
        }
      } else {
        dispatch({
          type: "ADD_LOG_ENTRY",
          payload: {
            message: "Error: Missing asset serial when reporting missing.",
          },
        });
      }
    } else {
      dispatch({
        type: "SHOW_TOAST",
        payload: {
          severity: "error",
          summary: "Error",
          detail:
            "An explanation of why the asset is being reported missing is required",
          life: 3000,
        },
      });
    }

    setTimeout(() => {
      SetCursorLoading(false);
    }, 0);
  };

  const handleAddPayment = async () => {
    SetCursorLoading(true);

    if (!paymentStudent) {
      dispatch({
        type: "SHOW_TOAST",
        payload: {
          severity: "error",
          summary: "Error",
          detail: "A student must be selected to add a payment.",
          life: 3000,
        },
      });      

      setTimeout(() => {
        SetCursorLoading(false);
      }, 0);
      return;
    }

    if (!paymentAmount || paymentAmount <= 0) {
      dispatch({
        type: "SHOW_TOAST",
        payload: {
          severity: "error",
          summary: "Error",
          detail: "A valid payment amount is required.",
          life: 3000,
        },
      });      

      setTimeout(() => {
        SetCursorLoading(false);
      }, 0);
      return;
    }

    if (paymentStudent && (paymentStudent.goalId === null || paymentStudent.goalId <= 0)) {
      dispatch({
        type: "SHOW_TOAST",
        payload: {
          severity: "error",
          summary: "Error",
          detail: "The selected student has an invalid GID.",
          life: 3000,
        },
      });      

      setTimeout(() => {
        SetCursorLoading(false);
      }, 0);
      return;
    }

    const apiResponse = await createUserPayment({  
      amount: paymentAmount ?? 0,
      notes: paymentNotes,
      payerGID: paymentStudent?.id ?? 0,
      inventoryObjectId: currentInventoryObject?.id ?? 0,
      receivedByGID: state.userGid,
      paymentDate: new Date(Date.now()),
    });

    if (apiResponse.statusCode === 200) {
      const successMsg: string = `Asset: ${assetSerial} - Add Payment succeeded.`;
      dispatch({
        type: "SHOW_TOAST",
        payload: {
          severity: "success",
          summary: "Success",
          detail: successMsg,
          life: 3000,
        },
      });
      dispatch({ type: "ADD_LOG_ENTRY", payload: { message: successMsg } });
      setPaymentDialogVisible(false);
    } else {
      const errorMsg: string = `Asset: ${assetSerial} - Add Payment failed. ${apiResponse.error}`;
      dispatch({
        type: "SHOW_TOAST",
        payload: {
          severity: "error",
          summary: "Error",
          detail: errorMsg,
          life: 3000,
        },
      });
      dispatch({ type: "ADD_LOG_ENTRY", payload: { message: errorMsg } });
    }

    setTimeout(() => {
      SetCursorLoading(false);
    }, 0);
  };

  const hasLessee = currentInventoryObject?.lesseeId ? true : false;

  const isDamaged = (currentInventoryObject?.repairStatusId ?? -1) > 0;
  const isMissing = (currentInventoryObject?.missingStatusId ?? -1) > 0;
  const isRetired = (currentInventoryObject?.retirementStatusId ?? -1) > 0;

  const handleDamageDialogHide = () => {
    setDamageDialogVisible(false);
  };

  const handleDamageDescriptionChange = (
    event: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    setDamageDescription(event.target.value);
  };

  const handleMissingDialogHide = () => {
    setMissingDialogVisible(false);
  };

  const handleMissingDescriptionChange = (
    event: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    setMissingDetails(event.target.value);
  };

  const handlePaymentDialogHide = () => {
    setPaymentDialogVisible(false);
  };

  const handlePaymentNotesChange = (
    event: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    setPaymentNotes(event.target.value);
  };

  useEffect(() => {
		async function fetchUser() {
			try {
				const response = await getUserById(state.userGid);

				if (response.data)
				{
					setCurrentUser(response.data);
				}
				else
				{
					const errorMsg: string = "Returned empty result when fetching user."
					console.error(errorMsg);
					dispatch({type: "SHOW_TOAST", payload: {severity: "error", summary: "Error", detail: errorMsg, life: 60000}});
					dispatch({type: "ADD_LOG_ENTRY", payload: {message: errorMsg}});
				}
			} catch (e) {
				const errorMsg: string = `Failed to fetch current user: ${e}`
				console.error(errorMsg);
				dispatch({type: "SHOW_TOAST", payload: {severity: "error", summary: "Error", detail: errorMsg, life: 60000}});
				dispatch({type: "ADD_LOG_ENTRY", payload: {message: errorMsg}});
			}
		}

		fetchUser();
	}, [state.userGid]);

  return (
    <div className="m-4 mb-2">
      <div className="flex flex-column md:flex-row gap-2 mb-2">
        <div className="w-full md:w-8">
          <Fieldset legend="Asset Details" className="mb-2">
            {currentInventoryObject && (
              <AssetDetailsPanel inventoryObject={currentInventoryObject} />
            )}
          </Fieldset>
          <Fieldset
            legend="Absolute"
            className="mb-2"
            toggleable
            collapsed={absoluteCollapsed}
            onToggle={(event) => handleAbsoluteToggle(event)}
          >
            {absoluteData && (
              <AssetAbsoluteDataPanel absoluteData={absoluteData} />
            )}
          </Fieldset>
          <Fieldset
            legend="Asset Payments"
            className="mb-2"
            toggleable
            collapsed={paymentsCollapsed}
            onToggle={(event) => handlePaymentsToggle(event)}
          >
            {!paymentsCollapsed && (
              <AssetPaymentsPanel
                assetId={currentInventoryObject?.id ?? 0}
                additionalActions={
                  <Button
                    label="Add Payment"
                    icon="pi pi-dollar"
                    onClick={() => setPaymentDialogVisible(true)}
                    loading={isCursorLoading}
                  />
                }
              />
            )}
            <Dialog
              header="Add Payment"
              onHide={handlePaymentDialogHide}
              visible={paymentDialogVisible}
            >
              <div className="w-full">
                <StudentSearchAutoComplete
                  label="Payer"
                  student={paymentStudent}
                  setStudent={setPaymentStudent}
                />
              </div>
              <div className="w-full">
                <label htmlFor="paymentAmountInputNumber">Payment Amount</label>
                <div className="w-full">
                  <InputNumber
                    id="paymentAmountInputNumber"
                    value={paymentAmount}
                    onValueChange={(e) => setPaymentAmount(e.value ?? null)}
                    minFractionDigits={2}
                    maxFractionDigits={2}
                    className="w-full"
                  />
                </div>
              </div>
              <div className="w-full">
                <label htmlFor="paymentNotesInputText">
                  Notes (optional)
                </label>
                <div className="w-full">
                  <InputTextarea
                    id="paymentNotesInputText"
                    value={paymentNotes}
                    onChange={(e) => handlePaymentNotesChange(e)}
                    className="w-full"
                  />
                </div>
              </div>
              <Button
                icon="pi pi-flag"
                severity="warning"
                label="Add Payment"
                onClick={handleAddPayment}
                className="mt-2 mr-2"
              />
              <Button
                label="Cancel"
                onClick={() => setPaymentDialogVisible(false)}
                className="mt-2"
              />
            </Dialog>
          </Fieldset>
        </div>
        <div className="w-full md:w-4">
          {hasLessee === true && (
            <Fieldset legend="Check In Asset" className="mb-2">
              <CheckInPanel isDisabled assetSerial={assetSerial} />
            </Fieldset>
          )}
          {hasLessee === false && (
            <Fieldset legend="Check Out Asset" className="mb-2">
              <CheckOutPanel isDisabled assetSerial={assetSerial} />
            </Fieldset>
          )}
          {currentInventoryObject && (
            <Fieldset legend="Status" className="mb-2">
              <div>
                <ul className="list-none p-0 m-0">
                  <AssetDataListElement
                    label="Repair State"
                    value={
                      currentInventoryObject.repairStatus?.name ?? "Undamaged"
                    }
                  />
                  <AssetDataListElement
                    label="Missing State"
                    value={
                      currentInventoryObject.missingStatus?.name ??
                      "Not Missing"
                    }
                  />
                  <AssetDataListElement
                    label="Retirement State"
                    value={
                      currentInventoryObject.retirementStatus?.name ??
                      "In Circulation"
                    }
                  />
                </ul>
                {isDamaged === false && (
                  <Button
                    label="Report Damaged"
                    icon="pi pi-wrench"
                    severity="danger"
                    onClick={() => setDamageDialogVisible(true)}
                    loading={isCursorLoading}
                  />
                )}
                <Dialog
                  header="Report Damaged"
                  onHide={handleDamageDialogHide}
                  visible={damageDialogVisible}
                >
                  <div className="w-full">
                    <label htmlFor="damageDescriptionInputText">
                      Description of Damage
                    </label>
                    <div className="w-full">
                      <InputTextarea
                        id="damageDescriptionInputText"
                        value={damageDescription}
                        onChange={(e) => handleDamageDescriptionChange(e)}
                        className="w-full"
                      />
                    </div>
                  </div>
                  <Button
                    icon="pi pi-wrench"
                    severity="danger"
                    label="Report Damaged"
                    onClick={handleReportDamaged}
                    className="mt-2 mr-2"
                  />
                  <Button
                    label="Cancel"
                    onClick={() => setDamageDialogVisible(false)}
                    className="mt-2"
                  />
                </Dialog>
                {isMissing === false && (
                  <Button
                    label="Report Missing"
                    icon="pi pi-flag"
                    severity="warning"
                    onClick={() => setMissingDialogVisible(true)}
                    loading={isCursorLoading}
                  />
                )}
                <Dialog
                  header="Report Missing"
                  onHide={handleMissingDialogHide}
                  visible={missingDialogVisible}
                >
                  <div className="w-full">
                    <label htmlFor="missingReasonInputText">
                      Reason for Report
                    </label>
                    <div className="w-full">
                      <InputTextarea
                        id="missingDescriptionInputText"
                        value={missingDetails}
                        onChange={(e) => handleMissingDescriptionChange(e)}
                        className="w-full"
                      />
                    </div>
                  </div>
                  <Button
                    icon="pi pi-flag"
                    severity="warning"
                    label="Report Missing"
                    onClick={handleReportMissing}
                    className="mt-2 mr-2"
                  />
                  <Button
                    label="Cancel"
                    onClick={() => setMissingDialogVisible(false)}
                    className="mt-2"
                  />
                </Dialog>
              </div>
            </Fieldset>
          )}
          <Fieldset
            legend="Asset History"
            toggleable
            collapsed={historyCollapsed}
            onToggle={(event) => handleAssetHistoryToggle(event)}
          >
            {assetHistory && (
              <AssetHistoryPanel inventoryOperations={assetHistory} />
            )}
          </Fieldset>
		  <Fieldset
			legend="Notes"
			className="mt-2"
			toggleable
			collapsed={notesCollapsed}
			onToggle={(event) => handleNotesToggle(event)}
		  >
			<NotesPanel inventoryObjectId={currentInventoryObject?.id ?? 0}></NotesPanel>
		  </Fieldset>
        </div>
      </div>
    </div>
  );
};

export default AssetManagementPage;
